'use client';

import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { PageHeader } from '@/components/ui/page-header';
import { toast } from '@/components/ui/use-toast';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useInvoices } from '@crm-src/hooks/useInvoices';
import { GenerateInvoiceForm } from '@crm-src/components/generate-invoice-form';
import {
  validateOttoInvoiceDraft,
  type OttoInvoiceDraft,
} from '@crm-src/components/generate-invoice-form/data-loaders';
import { consumeOttoDraft } from '@crm-src/lib/otto-draft-handoff';
import { BackButton } from "@/components/ui/back-button";

const GenerateQuotePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { createNewInvoice, getInvoiceStats } = useInvoices();
  const [isCreating, setIsCreating] = useState(false);
  const [preSelectedCustomerId, setPreSelectedCustomerId] = useState<string | null>(null);
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [duplicateFrom, setDuplicateFrom] = useState<string | null>(null);
  const [ottoDraft, setOttoDraft] = useState<OttoInvoiceDraft | null>(null);

  // Otto Agent handoff: a fully-formed quote draft stashed under `?draft=<nonce>`.
  useEffect(() => {
    const nonce = searchParams.get('draft');
    if (!nonce) return;
    try {
      const draft = validateOttoInvoiceDraft(consumeOttoDraft(nonce));
      setOttoDraft({ ...draft, document_type: 'quote' });
    } catch (error) {
      console.error('Invalid Otto quote draft:', error);
      toast({ title: 'Could not open the draft', description: 'Otto’s draft was incomplete.', variant: 'destructive' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Check for customerId, invoiceId, and duplicateFrom in URL parameters
  useEffect(() => {
    const customerId = searchParams.get('customerId');
    const editInvoiceId = searchParams.get('invoiceId');
    const duplicateFromId = searchParams.get('duplicateFrom'); // For duplicating quote


    if (customerId) {
      setPreSelectedCustomerId(customerId);
    }
    
    // If duplicating from quote, set it as duplicateFrom (NOT invoiceId)
    if (duplicateFromId) {
      setDuplicateFrom(duplicateFromId); // Load quote data for duplication
      setInvoiceId(null); // Keep in CREATE mode, not edit mode
    } else if (editInvoiceId) {
      setInvoiceId(editInvoiceId);
      setDuplicateFrom(null);
    }
  }, [searchParams]);

  // Handle form submission
  const handleSubmit = async (invoiceData: any) => {
    try {
      setIsCreating(true);
      
      await createNewInvoice(invoiceData);
      
      toast({
        title: 'Success',
        description: duplicateFrom ? 'Quote duplicated successfully' : 'Quote generated successfully'
      });

      // Refresh stats and redirect back appropriately
      // Refresh stats - filter for quotes only
    getInvoiceStats({ document_type: 'quote' });
      if (preSelectedCustomerId) {
        navigate(`/crm/customers/${preSelectedCustomerId}`);
      } else {
        navigate('/crm/quotes');
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error creating quote:', error);

      // Report server validation back to the form's persistent banner +
      // due-date field instead of an auto-dismissing toast.
      const data = error?.response?.data;
      let errors: string[];
      if (Array.isArray(error?.errors) && error.errors.length > 0) {
        errors = error.errors;
      } else if (Array.isArray(data?.errors) && data.errors.length > 0) {
        errors = data.errors;
      } else {
        errors = [
          error?.error || data?.error || error?.message || 'Failed to generate quote',
        ];
      }
      return { success: false, errors };
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancel = () => {
    if (preSelectedCustomerId) {
      navigate(`/crm/customers/${preSelectedCustomerId}`);
    } else {
      navigate('/crm/quotes');
    }
  };

  const handleBack = () => {
    if (preSelectedCustomerId) {
      navigate(`/crm/customers/${preSelectedCustomerId}`);
    } else {
      navigate('/quotes');
    }
  };

  const handleInvoiceUpdated = () => {
    toast({
      title: 'Success',
      description: 'Quote updated successfully'
    });

    // Refresh stats and redirect back appropriately
    // Refresh stats - filter for quotes only
    getInvoiceStats({ document_type: 'quote' });
    if (preSelectedCustomerId) {
      navigate(`/crm/customers/${preSelectedCustomerId}`);
    } else {
      navigate('/crm/quotes');
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title={invoiceId ? 'Edit Quote' : 'Generate Quote'}
        description={invoiceId ? 'Update quote details' : 'Create a professional quote with all details'}
        actions={
          <BackButton onClick={handleBack} />
        }
      />

      {/* Form Content */}
      <Box sx={{ bgcolor: "background.paper", p: 4, borderRadius: 1, boxShadow: 1, border: 1, borderColor: "divider" }}>
        <GenerateInvoiceForm
          onBack={handleBack}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          isCreating={isCreating}
          preSelectedCustomerId={preSelectedCustomerId}
          preSelectedDocumentType="quote"
          invoiceId={invoiceId}
          onInvoiceUpdated={handleInvoiceUpdated}
          duplicateFrom={duplicateFrom}
          ottoDraft={ottoDraft}
        />
      </Box>
    </Box>
  );
};

export default GenerateQuotePage;

